// ProductShowcase.tsx
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { FeaturesModal, FeaturesSection } from "./FeaturesModal";

// Clean Product Device Visual
function ProductVisual() {
  const image = "https://ojxcayuxakdkuqzpvpfp.supabase.co/storage/v1/object/public/images/Untitled%20design%20(2).png"

  return (
   <div className="relative w-full flex items-center justify-center py-12">
  {/* Glow Effect */}
  <motion.div
    className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
    animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.6, 0.3],
    }}
    transition={{
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />

  {/* Product Image with Floating Animation */}
  <motion.div
    className="relative z-10"
    animate={{
      y: [0, -15, 0],
    }}
    transition={{
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  >
    <img 
      src = {image} 
      alt="Oneect Restaurant Service Device"
      className="w-full max-w-lg h-auto drop-shadow-2xl"
    />
  </motion.div>
</div>
  );
}

export function ProductShowcase() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], [150, -150]);
  const contentY = useTransform(scrollYProgress, [0, 1], [100, -100]);

  const features = [
    {
      icon: "👋",
      title: "Call Waiter",
      description: "Instant notification sent directly to staff. No more waving or waiting.",
    },
    {
      icon: "💳",
      title: "Request Bill",
      description: "Quick payment requests that help turn tables faster.",
    },
    {
      icon: "🔕",
      title: "Silent Notification",
      description: "Discreet alerts that don't disturb other guests.",
    },
  ];


  return (
    <section 
      ref={ref} 
      className="relative min-h-screen flex items-center mt-20 px-6 overflow-hidden" 
      data-lenis-scroll
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Product Visual */}
          <motion.div style={{ y: imageY }}>
            <ProductVisual />
          </motion.div>

          {/* Right: Content */}
          <motion.div style={{ y: contentY }} className="space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold">
                ✨ Product Features
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold tracking-tight"
            >
              Designed for{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Effortless Service
              </span>
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              Transform your café experience with intuitive features designed for both customers and staff.
            </motion.p>

            {/* Features */}
            <div className="space-y-6">
              {features.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 + 0.3 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl flex items-center justify-center text-2xl">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="pt-4"
            >
              <motion.button
                onClick={() => setIsModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-black text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2"
              >
                See All Features
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </motion.button>
            </motion.div>
          </motion.div>
          <FeaturesModal
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-1/2 -right-40 w-80 h-80 bg-gradient-to-br from-blue-100/30 to-purple-100/30 rounded-full blur-3xl -z-10" />
    </section>
  );
}